import type { SurveyContactKey, SurveyFieldType } from '@engage/types';
import { Icon, ICONS } from '../../forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../forms/ui/tokens';

interface Item {
  type: SurveyFieldType;
  label: string;
}

interface PresetItem {
  type: SurveyFieldType;
  key: string;
  label: string;
  contact_key: SurveyContactKey;
}

// Contact-key presets. Tagging a field with `contact_key` lets the Task 6
// contact-sync worker create a NEW contact from a completed response (it never
// updates an existing profile). A survey with no tagged fields just collects
// data. Email is the match key — without it no contact is ever created.
const CONTACT_KEY_FIELDS: PresetItem[] = [
  { type: 'EMAIL', key: 'email', label: 'Email', contact_key: 'email' },
  { type: 'SHORT_TEXT', key: 'first_name', label: 'First name', contact_key: 'first_name' },
  { type: 'SHORT_TEXT', key: 'last_name', label: 'Surname', contact_key: 'last_name' },
  { type: 'SHORT_TEXT', key: 'company', label: 'Company', contact_key: 'company' },
];

const COMMON: Item[] = [
  { type: 'SHORT_TEXT', label: 'Short text' },
  { type: 'LONG_TEXT', label: 'Long text' },
  { type: 'EMAIL', label: 'Email' },
  { type: 'PHONE', label: 'Phone' },
  { type: 'NUMBER', label: 'Number' },
  { type: 'DATE', label: 'Date' },
];

const CHOICE: Item[] = [
  { type: 'SINGLE_SELECT', label: 'Single select' },
  { type: 'MULTI_SELECT', label: 'Multi select' },
];

const RATING: Item[] = [
  { type: 'NUMERIC_SCALE', label: 'Numeric scale' },
  { type: 'LIKERT', label: 'Likert scale' },
  { type: 'NPS', label: 'Net Promoter (NPS)' },
];

const PRIVACY: Item[] = [{ type: 'CONSENT_CHECKBOX', label: 'Consent checkbox' }];

export function FieldPalette(props: {
  pageSelected: boolean;
  onAdd: (type: SurveyFieldType) => void;
  onAddPreset: (preset: PresetItem) => void;
  existingKeys: string[];
}) {
  if (!props.pageSelected) {
    return (
      <div style={{ padding: '16px 14px', fontFamily: FORM_UI_FONT, fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
        Select or add a page to start adding questions.
      </div>
    );
  }
  return (
    <div style={{ padding: '16px 14px', fontFamily: FORM_UI_FONT }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.faint, letterSpacing: 0.4, marginBottom: 8 }}>
        QUESTION PALETTE
      </div>
      <PresetSection
        title="CONTACT FIELDS"
        note="Tag a field to create a new contact from completed responses."
        items={CONTACT_KEY_FIELDS}
        existingKeys={props.existingKeys}
        onAdd={props.onAddPreset}
      />
      <Section title="COMMON" items={COMMON} onAdd={props.onAdd} />
      <Section title="CHOICE" items={CHOICE} onAdd={props.onAdd} />
      <Section title="RATING" items={RATING} onAdd={props.onAdd} />
      <Section title="PRIVACY" items={PRIVACY} onAdd={props.onAdd} />
    </div>
  );
}

function PresetSection(props: {
  title: string;
  note: string;
  items: PresetItem[];
  existingKeys: string[];
  onAdd: (preset: PresetItem) => void;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: C.indigoSoft, margin: '6px 4px', letterSpacing: 0.3 }}>
        {props.title}
      </div>
      <div style={{ fontSize: 10.5, color: C.faint, margin: '0 4px 6px', lineHeight: 1.4 }}>{props.note}</div>
      {props.items.map((item) => {
        const added = props.existingKeys.includes(item.key);
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => props.onAdd(item)}
            disabled={added}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 9,
              padding: '8px 10px',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 500,
              color: added ? C.faint : C.ink,
              cursor: added ? 'default' : 'pointer',
              border: `1px solid ${added ? C.line : C.teal}`,
              marginBottom: 5,
              background: added ? C.bg : '#fff',
              fontFamily: FORM_UI_FONT,
              textAlign: 'left',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Icon d={added ? ICONS.check : ICONS.plus} size={13} color={added ? C.faint : C.teal} sw={2.4} />
              {item.label}
            </span>
            {added && <span style={{ fontSize: 10.5 }}>Added</span>}
          </button>
        );
      })}
    </div>
  );
}

function Section(props: { title: string; items: Item[]; onAdd: (type: SurveyFieldType) => void }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: C.indigoSoft, margin: '6px 4px', letterSpacing: 0.3 }}>
        {props.title}
      </div>
      {props.items.map((item) => (
        <button
          key={item.type}
          type="button"
          onClick={() => props.onAdd(item.type)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 500,
            color: C.ink,
            cursor: 'pointer',
            border: `1px solid ${C.line}`,
            marginBottom: 5,
            background: '#fff',
            fontFamily: FORM_UI_FONT,
            textAlign: 'left',
          }}
        >
          <Icon d={ICONS.plus} size={13} color={C.teal} sw={2.4} />
          {item.label}
        </button>
      ))}
    </div>
  );
}
